import { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { PlayCircle } from 'lucide-react';
import { buildImageUrl } from '@/lib/utils';

function getInitials(name) {
  return String(name || '')
    .split(' ')
    .map((part) => part?.[0] || '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function pickText(lang, arText, enText) {
  return lang === 'ar' ? arText : enText;
}

function TeacherMediaCard({ teacher, lang, badgeLabel }) {
  const shouldReduceMotion = useReducedMotion();
  const [imageFailed, setImageFailed] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
    setVideoFailed(false);
  }, [teacher?.image_url, teacher?.video_url]);

  const imageUrl = useMemo(() => {
    if (imageFailed) return '';
    return buildImageUrl(teacher?.image_url || '');
  }, [imageFailed, teacher?.image_url]);

  const videoUrl = useMemo(() => {
    if (videoFailed) return '';
    return buildImageUrl(teacher?.video_url || '');
  }, [videoFailed, teacher?.video_url]);

  const hasImage = Boolean(imageUrl);
  const hasVideo = Boolean(videoUrl);
  const isExternalVideo = /^https?:\/\//i.test(videoUrl) && !videoUrl.includes('/api/content/teachers/');
  const initials = getInitials(teacher?.name) || 'BF';

  return (
    <motion.article
      initial={shouldReduceMotion ? false : { opacity: 0, y: 28 }}
      whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={shouldReduceMotion ? undefined : { duration: 0.5 }}
    >
      <Card className="bf-teacher-card overflow-hidden border-0 shadow-none">
        <CardContent className="p-0">
          <div className="bf-teacher-card-layout">
            <div className={`bf-teacher-media-column ${hasVideo ? 'has-video' : ''}`}>
              <div className="bf-teacher-portrait-shell">
                {hasImage ? (
                  <img
                    src={imageUrl}
                    alt={teacher?.name || pickText(lang, '\u0635\u0648\u0631\u0629 \u0627\u0644\u0645\u062f\u0631\u0651\u0633', 'Teacher portrait')}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={() => setImageFailed(true)}
                  />
                ) : (
                  <div className="bf-teacher-media-fallback">
                    <div className="bf-teacher-placeholder-avatar">{initials}</div>
                    <div className="bf-teacher-fallback-copy">
                      <p className="bf-ui-label text-[#dce6f8]">
                        {pickText(lang, '\u0645\u0644\u0641 \u062a\u0639\u0631\u064a\u0641\u064a', 'Profile')}
                      </p>
                      <p className="mt-2 text-sm leading-7 text-white/74">
                        {pickText(
                          lang,
                          '\u0635\u0648\u0631\u0629 \u0627\u0644\u0645\u062f\u0631\u0651\u0633 \u063a\u064a\u0631 \u0645\u062a\u0627\u062d\u0629 \u062d\u0627\u0644\u064a\u0627\u064b.',
                          'Teacher portrait is currently unavailable.'
                        )}
                      </p>
                    </div>
                  </div>
                )}
                <div className="bf-teacher-media-overlay" />
              </div>

              {hasVideo ? (
                <div className="bf-teacher-video-shell">
                  <div className="bf-teacher-video-head">
                    <PlayCircle className="h-4 w-4" />
                    <span>{pickText(lang, '\u0641\u064a\u062f\u064a\u0648 \u062a\u0639\u0631\u064a\u0641\u064a', 'Introduction video')}</span>
                  </div>
                  {isExternalVideo ? (
                    <a
                      href={videoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="bf-teacher-video-link"
                    >
                      <span>{pickText(lang, '\u0645\u0634\u0627\u0647\u062f\u0629 \u0627\u0644\u0641\u064a\u062f\u064a\u0648', 'Watch video')}</span>
                    </a>
                  ) : (
                    <video
                      controls
                      preload="metadata"
                      poster={hasImage ? imageUrl : undefined}
                      src={videoUrl}
                      className="bf-teacher-video-player"
                      onError={() => setVideoFailed(true)}
                    />
                  )}
                </div>
              ) : null}
            </div>

            <div className="bf-teacher-content">
              <div className="bf-teacher-copy">
                <div className="bf-teacher-pill">
                  <span>{badgeLabel}</span>
                </div>
                <h3 className="bf-teacher-name text-[#1c2333] dark:text-[#f4f5f7]">
                  {teacher?.name || pickText(lang, '\u0645\u062f\u0631\u0651\u0633 Be Fluent', 'Be Fluent Teacher')}
                </h3>
                {teacher?.title ? (
                  <p className="bf-teacher-role">{teacher.title}</p>
                ) : null}
                <p className="bf-teacher-bio">
                  {teacher?.short_bio || pickText(
                    lang,
                    '\u0633\u064a\u0638\u0647\u0631 \u0648\u0635\u0641 \u0627\u0644\u0645\u062f\u0631\u0651\u0633 \u0628\u0639\u062f \u0646\u0634\u0631 \u0627\u0644\u0645\u0644\u0641 \u0645\u0646 \u0644\u0648\u062d\u0629 \u0627\u0644\u0645\u0648\u0642\u0639.',
                    'Instructor profile copy will appear once the website profile is published.'
                  )}
                </p>
              </div>

              <div className="bf-teacher-footnote">
                <div className="bf-teacher-mini-avatar">{initials}</div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1c2333] dark:text-[#f4f5f7]">
                    {teacher?.name || pickText(lang, '\u0647\u064a\u0626\u0629 \u0627\u0644\u062a\u062f\u0631\u064a\u0633', 'Teaching Faculty')}
                  </p>
                  <p className="text-xs tracking-[0.01em] text-[#1c2333]/58 dark:text-[#f4f5f7]/60">
                    {hasVideo
                      ? (isExternalVideo
                        ? pickText(lang, '\u0631\u0627\u0628\u0637 \u0641\u064a\u062f\u064a\u0648 \u062e\u0627\u0631\u062c\u064a', 'External introduction video link')
                        : pickText(lang, '\u064a\u062a\u0636\u0645\u0646 \u0641\u064a\u062f\u064a\u0648 \u062a\u0639\u0631\u064a\u0641\u064a \u0641\u0639\u0651\u0627\u0644', 'Includes a live introduction video'))
                      : pickText(lang, '\u0635\u0648\u0631\u0629 \u0648\u0647\u0648\u064a\u0629 \u062a\u0639\u0631\u064a\u0641\u064a\u0629 \u0645\u0646\u0634\u0648\u0631\u0629', 'Published portrait and profile')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.article>
  );
}

export function TeacherShowcaseSection({
  teachers,
  lang,
  eyebrow,
  title,
  subtitle,
  badgeLabel,
  className = '',
}) {
  if (!teachers?.length) return null;

  return (
    <section className={`bf-teacher-section px-4 py-14 sm:px-6 lg:px-8 lg:py-20 ${className}`.trim()}>
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 max-w-3xl">
          <p className="bf-brand-kicker text-[#6c8bc6]">{eyebrow}</p>
          <h2 className="bf-section-title mt-3 text-[#1c2333] dark:text-[#f4f5f7]">{title}</h2>
          <p className="mt-4 max-w-2xl text-base leading-8 text-[#1c2333]/72 dark:text-[#f4f5f7]/72">
            {subtitle}
          </p>
        </div>

        <div className="grid gap-6">
          {teachers.map((teacher) => (
            <TeacherMediaCard
              key={teacher.id || `${teacher.name || 'teacher'}-${teacher.sequence || 'item'}`}
              teacher={teacher}
              lang={lang}
              badgeLabel={badgeLabel}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
